import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

export const useCookbooks = ({ activeApi, token, setConfirmationModal, onCookbookDeleted }) => {
  const [cookbooks, setCookbooks] = useState([]);
  const [isCookbookModalOpen, setIsCookbookModalOpen] = useState(false);
  const [editingCookbook, setEditingCookbook] = useState(null);
  const [cookbookSearchTerm, setCookbookSearchTerm] = useState('');
  const [cookbookPage, setCookbookPage] = useState(1);
  const [selectedCookbookIds, setSelectedCookbookIds] = useState([]);
  const hasFetchedData = useRef(false);

  useEffect(() => {
    if (token) {
      if (!hasFetchedData.current) {
        hasFetchedData.current = true;
        activeApi.getCookbooks().then(setCookbooks).catch(error => console.error("Error fetching cookbooks:", error));
      }
    } else {
      hasFetchedData.current = false;
    }
  }, [token, activeApi]);

  const addCookbook = useCallback(async (cookbook, andAnother) => {
    const isEditing = !!editingCookbook;
    if (cookbooks.find(c => c.title.toLowerCase() === cookbook.title.toLowerCase() && c.id !== cookbook.id)) {
      return { error: 'A cookbook with this name already exists.' };
    }
    try {
      const savedCookbook = isEditing
        ? await activeApi.updateCookbookAPI(editingCookbook.id, cookbook)
        : await activeApi.addCookbookAPI(cookbook);

      if (isEditing) {
        setCookbooks(prev => prev.map(c => (c.id === savedCookbook.id ? savedCookbook : c)));
        setEditingCookbook(null);
      } else {
        setCookbooks(prev => [...prev, savedCookbook]);
      }
      if (!andAnother) {
        setIsCookbookModalOpen(false);
      }
      return { success: true, cookbook: savedCookbook };
    } catch (error) {
      return { error: error.message };
    }
  }, [cookbooks, editingCookbook, activeApi]);

  const deleteCookbook = useCallback((cookbookId) => {
    const cookbookToDelete = cookbooks.find(c => c.id === cookbookId);
    const message = cookbookToDelete
      ? `Are you sure you want to delete cookbook '${cookbookToDelete.title}'?`
      : 'Are you sure you want to delete this cookbook?';

    setConfirmationModal({
      isOpen: true,
      message,
      onConfirm: () => {
        activeApi.deleteCookbookAPI(cookbookId)
          .then(() => {
            const newCookbooks = cookbooks.filter(c => c.id !== cookbookId);
            setCookbooks(newCookbooks);
            const totalPages = Math.ceil(newCookbooks.length / 5);
            if (cookbookPage > totalPages) {
              setCookbookPage(totalPages || 1);
            }
            if (onCookbookDeleted) {
              onCookbookDeleted(cookbookId);
            }
          })
          .catch(error => console.error('Error deleting cookbook:', error))
          .finally(() => setConfirmationModal({ isOpen: false, message: '', onConfirm: () => { } }));
      }
    });
  }, [activeApi, cookbooks, cookbookPage, setConfirmationModal, onCookbookDeleted]);

  const startEditingCookbook = useCallback((cookbook) => {
    setEditingCookbook(cookbook);
    setIsCookbookModalOpen(true);
  }, []);

  const cancelEditingCookbook = useCallback(() => {
    setEditingCookbook(null);
    setIsCookbookModalOpen(false);
  }, []);

  const openAddCookbookModal = useCallback(() => {
    setEditingCookbook(null);
    setIsCookbookModalOpen(true);
  }, []);

  const handleCookbookSelection = useCallback((cookbookId, operation = 'OR') => {
    setSelectedCookbookIds(prev => {
      // Normalize existing items to objects for safety if mixed
      const currentItems = prev.map(item => (typeof item === 'string' ? { id: item, operation: 'OR' } : item));
      const existingIndex = currentItems.findIndex(item => item.id === cookbookId);

      if (existingIndex >= 0) {
        const existingItem = currentItems[existingIndex];
        if (existingItem.operation === operation) {
          // Toggle off
          return currentItems.filter((_, index) => index !== existingIndex);
        } else {
          // Change operation
          const newItems = [...currentItems];
          newItems[existingIndex] = { id: cookbookId, operation };
          return newItems;
        }
      } else {
        // Add new
        return [...currentItems, { id: cookbookId, operation }];
      }
    });
  }, []);

  const unselectAllCookbooks = useCallback(() => {
    setSelectedCookbookIds([]);
  }, []);

  const handleSearchChange = useCallback((term) => {
    setCookbookSearchTerm(term);
    setCookbookPage(1);
  }, []);

  const filteredCookbooks = useMemo(() =>
    cookbooks.filter(cookbook =>
      cookbook && cookbook.title && cookbook.title.toLowerCase().includes(cookbookSearchTerm.toLowerCase())
    ), [cookbooks, cookbookSearchTerm]
  );

  return {
    cookbooks,
    setCookbooks,
    filteredCookbooks,
    isCookbookModalOpen,
    setIsCookbookModalOpen,
    editingCookbook,
    cookbookSearchTerm,
    cookbookPage, setCookbookPage,
    selectedCookbookIds, setSelectedCookbookIds,
    addCookbook,
    deleteCookbook,
    startEditingCookbook,
    cancelEditingCookbook,
    openAddCookbookModal,
    handleCookbookSelection,
    unselectAllCookbooks,
    handleSearchChange,
  };
};